import React, { useMemo } from 'react';
import {
  PERSIAN_MONTHS,
  getCurrentPersianYearMonth,
  getCurrentPersianDate,
  persianDaysInMonth,
} from '../../../shared/utils/persianDate';

const PERIOD_OPTIONS = [
  { value: 'daily',     label: 'روزانه' },
  { value: 'weekly',    label: 'هفتگی' },
  { value: 'monthly',   label: 'ماهانه' },
  { value: 'quarterly', label: 'فصلی' },
  { value: 'yearly',    label: 'سالانه' },
];

const YEAR_RANGE_BEFORE = 2;
const YEAR_RANGE_AFTER = 2;

function buildYearOptions(currentYear) {
  const years = [];
  for (let y = currentYear - YEAR_RANGE_BEFORE; y <= currentYear + YEAR_RANGE_AFTER; y += 1) {
    years.push(y);
  }
  return years;
}

function Chip({ label, children }) {
  return (
    <label className="flex h-6 cursor-pointer items-center gap-1 rounded-full bg-white px-1.5 shadow-sm ring-1 ring-slate-200/80 transition-shadow hover:ring-indigo-200">
      <span className="text-[10px] font-bold text-slate-400">{label}</span>
      {children}
    </label>
  );
}

export default function ReportPeriodFilter({ value = {}, onChange }) {
  const { year: currentYear } = getCurrentPersianYearMonth();
  const today = getCurrentPersianDate();
  const years = useMemo(() => buildYearOptions(currentYear), [currentYear]);
  const { year = currentYear, month = 1, period = 'monthly' } = value;
  const daysInMonth = persianDaysInMonth(year, month);
  const defaultDay = year === today.year && month === today.month ? today.day : daysInMonth;
  const day = Math.min(Math.max(1, Number(value.day) || defaultDay), daysInMonth);

  const set = (patch) => onChange({ ...value, year, month, period, day, ...patch });

  const selectCls =
    'h-5 appearance-none border-0 bg-transparent py-0 pl-3 pr-0.5 text-xs font-bold text-slate-800 focus:outline-none cursor-pointer';

  const grainHint = period !== 'monthly'
    ? 'بازه روی جدول و نمودار اثر دارد؛ KPI فقط سال و ماه را می‌خواند.'
    : 'سال و ماه برای همه ویجت‌ها اعمال می‌شود.';

  return (
    <div
      className="inline-flex h-7 max-w-full flex-wrap items-center gap-1 rounded-full border border-slate-200/90 bg-gradient-to-l from-slate-50 to-white px-1"
      dir="rtl"
      title={grainHint}
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-50 text-[11px] text-indigo-600" aria-hidden>
        📅
      </span>

      <Chip label="بازه">
        <select className={selectCls} value={period} onChange={(e) => set({ period: e.target.value })}>
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Chip>

      <Chip label="سال">
        <select className={selectCls} value={year} onChange={(e) => set({ year: Number(e.target.value) })}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </Chip>

      <Chip label="ماه">
        <select className={selectCls} value={month} onChange={(e) => set({ month: Number(e.target.value) })}>
          {PERSIAN_MONTHS.map((label, idx) => (
            <option key={idx + 1} value={idx + 1}>{label}</option>
          ))}
        </select>
      </Chip>

      {period === 'daily' && (
        <Chip label="روز">
          <select
            className={selectCls}
            value={day}
            onChange={(e) => {
              const d = Number(e.target.value);
              set({ day: d, as_of_day: d });
            }}
          >
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </Chip>
      )}

      <button
        type="button"
        className="h-6 rounded-full bg-indigo-600 px-2 text-[10px] font-bold text-white shadow-sm transition-all hover:bg-indigo-700 active:scale-[0.98]"
        onClick={() => onChange({ ...value, year, month, period, day })}
      >
        بروزرسانی
      </button>
    </div>
  );
}
